import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Box,
} from '@mui/material';
import { rightDirectionIcon, leftDirectionIcon } from '../../../assets';
import { getFacilities } from '../../../utils/getToken';
import { getFacilities as fetchFacilities } from '../../../screens/patient-screen/patientSaga';
import { componentKey } from '../../../screens/patient-screen/patientSlice';
import { switchToAdmin } from '../../../utils/roleSwitch';

interface FacilityDropdownProps {
  onFacilitySelect?: (facility: any) => void;
  variant?: 'desktop' | 'mobile';
  showDropdown?: boolean; // New prop to control dropdown visibility
}

const FacilityDropdown: React.FC<FacilityDropdownProps> = ({
  onFacilitySelect,
  variant = 'desktop',
  showDropdown = true // Default to showing dropdown for backward compatibility
}) => {
  const dispatch = useDispatch();
  const { facilityData } = useSelector((state: any) => state[componentKey]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // Prefer the freshly-fetched list; fall back to the login (localStorage) list.
  const facilities: any[] =
    facilityData && facilityData.length > 0 ? facilityData : getFacilities() || [];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Refresh the full facility list so newly-added facilities appear without
    // a re-login (localStorage only holds what was captured at login).
    dispatch(fetchFacilities());
    if (showDropdown) {
      setAnchorEl(event.currentTarget);
    } else {
      // Direct switch to admin without dropdown
      handleDirectSwitchToAdmin();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDirectSwitchToAdmin = () => {
    // Get the first available facility
    const firstFacility = facilities && facilities.length > 0 ? facilities[0] : null;
    
    if (!firstFacility) {
      console.error('No facilities available for admin switch');
      return;
    }

    // Call the onFacilitySelect callback if provided
    if (onFacilitySelect) {
      onFacilitySelect(firstFacility);
    }
    
    // Navigate to admin with refresh token and facility ID
    const success = switchToAdmin(firstFacility.id);
    if (!success) {
      console.error('Failed to switch to admin');
    }
  };

  const handleFacilitySelect = (facility: any) => {
    // Call the onFacilitySelect callback if provided
    if (onFacilitySelect) {
      onFacilitySelect(facility);
    }
    
    // Navigate to admin with refresh token and facility ID
    const success = switchToAdmin(facility.id);
    if (!success) {
      console.error('Failed to switch to admin');
    }
    
    handleClose();
  };

  const buttonSx = variant === 'desktop' 
    ? {
        paddingTop: "6px",
        paddingBottom: "6px",
        minWidth: "180px",
        height: "38px",
        border: "1px solid #005596",
        color: "#005596",
        backgroundColor: "#FFFFFF",
        textTransform: "none" as const,
        fontSize: "14px",
        fontWeight: 500,
      }
    : {
        paddingTop: "8px",
        paddingBottom: "8px",
        height: "40px",
        border: "1px solid #005596",
        color: "#005596",
        backgroundColor: "#FFFFFF",
        textTransform: "none" as const,
        fontSize: "14px",
        fontWeight: 500,
        width: "100%",
      };

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={
          <div className="flex flex-col cursor-pointer items-center mr-2 -space-y-0.5 space-x-2">
            <img src={rightDirectionIcon} alt="switch" className="w-3 h-3" />
            <img src={leftDirectionIcon} alt="switch" className="w-3 h-3" />
          </div>
        }
        onClick={handleClick}
        sx={buttonSx}
      >
        Switch to Admin
      </Button>
      
      {/* Conditionally render dropdown menu */}
      {showDropdown && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              minWidth: variant === 'desktop' ? '180px' : '280px',
              maxHeight: '300px',
              overflow: 'auto',
            }
          }}
        >
          {facilities && facilities.length > 0 ? (
            facilities.map((facility: any) => (
              <MenuItem
                key={facility.id}
                onClick={() => handleFacilitySelect(facility)}
                sx={{
                  padding: '8px 16px',
                  fontSize: '14px',
                }}
              >
                <ListItemText primary={facility.name} />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText primary="No facilities available" />
            </MenuItem>
          )}
        </Menu>
      )}
    </Box>
  );
};

export default FacilityDropdown;

